import type { Stripe } from '@stripe/stripe-js';
import { getApiClientErrorDetailFromError, getUserFacingErrorMessage } from '@/services/api/errors';
import { billing } from '@/services/billing';
import { IS_STRIPE_TEST_MODE } from '@/services/billing/stripe/elements';
import {
  confirmPurchasePayment,
  WalletPurchaseInProgressError,
  WalletPurchaseTimedOutError,
} from '@/services/billing/wallet-purchase';
import { sentryCaptureException } from '@/utils/sentry';

/** The self-serve catalog product the server prices and bills. */
const AGENT_RESET_PRODUCT = 'agent_reset';

export const AGENT_RESET_NOT_CREATED_MESSAGE = "Your purchase couldn't be created. Try again.";
// The shared poll's timeout message is about a wallet balance, which a reset does not change.
export const AGENT_RESET_TIMED_OUT_MESSAGE =
  "Your payment is processing. Contact support if your agent usage doesn't reset.";

/** Only the method the purchase calls, so a test can stub it. */
export type ConfirmingStripe = Pick<Stripe, 'confirmCardPayment'>;

/**
 * Buys the agent usage reset on the card on file. The server decides the price from the share of
 * the allocation used, and the tax from the account's billing address, so the request names only
 * the product.
 *
 * The server refuses a purchase without a billing address, and its reason is what this throws.
 *
 * Resolves `true` once the reset is paid and `false` when the poll is cancelled. Throws the message
 * to show.
 */
export async function purchaseAgentUsageReset(loadStripe: () => Promise<ConfirmingStripe | null>): Promise<boolean> {
  let runId: string;
  try {
    runId = await billing.startWalletPurchase({ one_time: { product: AGENT_RESET_PRODUCT } });
  } catch (error) {
    if (error instanceof WalletPurchaseInProgressError) {
      throw error;
    }
    if (getApiClientErrorDetailFromError(error) == null) {
      sentryCaptureException(error);
    }
    throw new Error(getUserFacingErrorMessage(error, AGENT_RESET_NOT_CREATED_MESSAGE));
  }

  try {
    const completed = await billing.pollForOneTimePurchaseTask.perform(runId, (secret) =>
      confirmPayment(loadStripe, secret),
    );
    return completed === true;
  } catch (error) {
    if (error instanceof WalletPurchaseTimedOutError) {
      throw new Error(AGENT_RESET_TIMED_OUT_MESSAGE);
    }
    throw error;
  }
}

// Stripe.js loads only when a purchase needs it. A failed load reaches the confirmation as no Stripe.
async function confirmPayment(loadStripe: () => Promise<ConfirmingStripe | null>, clientSecret: string): Promise<void> {
  if (IS_STRIPE_TEST_MODE) {
    return;
  }

  const stripe = await loadStripe().catch((error: unknown) => {
    sentryCaptureException(error);
    return null;
  });
  await confirmPurchasePayment(stripe, clientSecret);
}
